/**
 * AuthorizedContactsListView — visão **tenant-wide** dos contatos autorizados
 * (oversight do DPO · Centro de Direitos do Titular, Brief 5 B1).
 *
 * Distinto do drawer bond-scoped do TabCliente (gestão dentro de uma conversa):
 * aqui o DPO fiscaliza TODAS as autorizações LGPD do tenant num só lugar —
 * quem autorizou, em qual vínculo (tipo), escopos, status, expiração — e pode
 * revogar com motivo. Caso real do DPO de saúde/jurídico.
 *
 * Gate honesto (gatear o fetch, não só o render): sem `lgpd-authorized-contacts-read`
 * → nota, sem disparar o GET. Revogação reusa o DELETE bond-scoped (If-Match).
 * P15: sem CPF — só o nome do titular (displayName).
 */

import { useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import {
  useRevokeTenantAuthorizedContact,
  useTenantAuthorizedContacts,
} from '../../../hooks/use-tenant-authorized-contacts';
import {
  formatDate,
  scopeLabel,
  statusLabel,
} from '../../bonds/authorized-contacts/authorized-contacts-labels';
import {
  deriveMandateValidity,
  isScopeInert,
} from '../../bonds/authorized-contacts/scope-effectiveness';

import type { TenantAuthorizedContactWireItem } from '../../../lib/api-client';

const READ_CAP = 'lgpd-authorized-contacts-read';
const MANAGE_CAP = 'lgpd-authorized-contacts-manage';

export function AuthorizedContactsListView(): JSX.Element {
  const canRead = useCapability(READ_CAP);
  const [includeRevoked, setIncludeRevoked] = useState(false);

  // Gatear o FETCH na cap (não só o render): só dispara o GET com a cap.
  const { data, isLoading, isError } = useTenantAuthorizedContacts(includeRevoked, canRead);

  if (!canRead) {
    return (
      <EmptyNote>
        Seu perfil não tem acesso à gestão de contatos autorizados do tenant (restrita ao DPO). A
        gestão segue disponível na aba Cliente de cada conversa.
      </EmptyNote>
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <span style={{ fontSize: 12, color: '#6b7280' }}>
          {data.length} {data.length === 1 ? 'autorização' : 'autorizações'}
          {includeRevoked ? ' (inclui histórico)' : ' ativas'}
        </span>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: '#374151',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={includeRevoked}
            onChange={(e) => {
              setIncludeRevoked(e.target.checked);
            }}
          />
          Incluir revogados/expirados
        </label>
      </div>

      {isLoading && <EmptyNote>Carregando autorizações…</EmptyNote>}
      {isError && !isLoading && (
        <EmptyNote tone="error">Erro ao carregar contatos autorizados. Tente recarregar.</EmptyNote>
      )}
      {!isLoading && !isError && data.length === 0 && (
        <EmptyNote>
          Nenhum contato autorizado {includeRevoked ? 'registrado' : 'ativo'} neste tenant.
        </EmptyNote>
      )}

      {!isLoading && !isError && data.length > 0 && (
        <div
          role="list"
          aria-label="Contatos autorizados do tenant"
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          {data.map((c) => (
            <AuthorizedContactRow key={c.id} contact={c} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Row ───────────────────────────────────────────────────────────────────────

function AuthorizedContactRow({
  contact,
}: {
  contact: TenantAuthorizedContactWireItem;
}): JSX.Element {
  const canManage = useCapability(MANAGE_CAP);
  const revoke = useRevokeTenantAuthorizedContact();
  const [revoking, setRevoking] = useState(false);
  const [reason, setReason] = useState('');

  const isActive = contact.status === 'active';
  /**
   * **Motor Neutro D1** — o estado que o MOTOR vê. O banco nunca escreve `'expired'` (R-B6-b: o
   * forfeit é resolvido em tempo de leitura), então a fiscalização do DPO lia «ATIVO» sobre
   * mandato caducado. Derivado pela função do caminho de autorização (`resolveVoluntaryGrant`),
   * nunca por uma 2ª comparação de datas. A ação de revogar segue a coluna `status` — revogar um
   * mandato já caducado continua fazendo sentido (encerra o instrumento).
   */
  const validity = deriveMandateValidity(contact);
  const statusColor =
    validity === 'active' ? '#16a34a' : validity === 'revoked' ? '#ef4444' : '#b45309';

  function handleConfirmRevoke(): void {
    if (reason.trim().length === 0) return;
    revoke.mutate(
      {
        bondId: contact.bondId,
        contactId: contact.id,
        version: contact.version,
        body: { revocationReason: reason.trim() },
      },
      {
        onSuccess: () => {
          setRevoking(false);
          setReason('');
        },
      },
    );
  }

  return (
    <div
      role="listitem"
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: '12px 14px',
        background: '#fff',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
            {contact.personName}
          </div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>
            Vínculo: {contact.bondType} · desde {formatDate(contact.createdAt)}
            {contact.expiresAt !== null && ` · expira ${formatDate(contact.expiresAt)}`}
          </div>
        </div>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: statusColor,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          {statusLabel(validity)}
        </span>
        {isActive && canManage && !revoking && (
          <button
            type="button"
            onClick={() => {
              setRevoking(true);
            }}
            style={{
              padding: '4px 10px',
              fontSize: 11,
              fontWeight: 600,
              color: '#b91c1c',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Revogar
          </button>
        )}
      </div>

      {/* Escopos */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
        {contact.scopes.map((s) => {
          // D1 — escopo concedido que nenhuma ação exige (C1: 16 das 21 chaves). A fiscalização
          // do DPO precisa saber que a autorização listada não confere poder algum hoje.
          const inert = isScopeInert(s);
          return (
            <span
              key={s}
              title={
                inert
                  ? 'Nenhuma ação da plataforma exige esta chave hoje — este escopo não muda o que o contato pode fazer.'
                  : undefined
              }
              style={{
                fontSize: 10,
                padding: '1px 8px',
                borderRadius: 20,
                background: inert ? '#fffbeb' : '#eff6ff',
                border: `1px solid ${inert ? '#fde68a' : '#bfdbfe'}`,
                color: inert ? '#b45309' : '#1d4ed8',
              }}
            >
              {scopeLabel(s)}
              {inert && <strong style={{ marginLeft: 4 }}>· sem efeito</strong>}
            </span>
          );
        })}
      </div>

      {/* Motivo da revogação (histórico) */}
      {contact.status === 'revoked' && contact.revocationReason !== null && (
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6, fontStyle: 'italic' }}>
          Revogado: {contact.revocationReason}
        </div>
      )}

      {/* Inline revoke form */}
      {revoking && (
        <div style={{ marginTop: 10, borderTop: '1px dashed #e5e7eb', paddingTop: 10 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>
            Motivo da revogação (auditado)
          </label>
          <textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            rows={2}
            placeholder="Ex.: solicitação do titular / autorização expirada"
            style={{
              width: '100%',
              marginTop: 4,
              fontSize: 12,
              padding: 8,
              border: '1px solid #d1d5db',
              borderRadius: 6,
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => {
                setRevoking(false);
                setReason('');
              }}
              disabled={revoke.isPending}
              style={{
                padding: '5px 12px',
                fontSize: 12,
                color: '#374151',
                background: '#fff',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmRevoke}
              disabled={reason.trim().length === 0 || revoke.isPending}
              style={{
                padding: '5px 12px',
                fontSize: 12,
                fontWeight: 600,
                color: '#fff',
                background: reason.trim().length === 0 ? '#fca5a5' : '#dc2626',
                border: 'none',
                borderRadius: 6,
                cursor: reason.trim().length === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              {revoke.isPending ? 'Revogando…' : 'Confirmar revogação'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── EmptyNote ───────────────────────────────────────────────────────────────

function EmptyNote({ children, tone }: { children: React.ReactNode; tone?: 'error' }): JSX.Element {
  return (
    <div
      style={{
        padding: '32px 24px',
        textAlign: 'center',
        color: tone === 'error' ? '#ef4444' : '#9ca3af',
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}
